import {
  UploadAirframeTemplateRequest,
  AirframeTemplateMetadataDto,
  AirframeTemplateDetailsDto,
  DownloadAssetTemplateRequest,
  AssetTemplateFileType,
  BulkTemplateUploadValidationResult,
  BulkTemplateUploadProcessResult,
  FolderProvisioningStatus,
} from '../types/airframeTemplate';
import { useCallback } from 'react';
import { useApiService } from './index';

export const useAirframeTemplateService = () => {
  const { post, get, postBlob } = useApiService();

  const buildBulkUploadPayload = (fileType: AssetTemplateFileType, file: File) => {
    const payload = new FormData();
    payload.append('fileType', fileType);
    payload.append('file', file);
    return payload;
  };

  const uploadAirframeTemplate = async (data: UploadAirframeTemplateRequest) => {
    const response = await post('/api/assets-templates', data);
    return response.data;
  };

  const getAirframeTemplates = useCallback(async (): Promise<AirframeTemplateMetadataDto[]> => {
    const response = await get('/api/assets-templates');
    return response.data;
  }, [get]);

  const getAirframeTemplateById = useCallback(async (id: string): Promise<AirframeTemplateDetailsDto> => {
    const response = await get(`/api/assets-templates/${id}`);
    return response.data;
  }, [get]);

  const downloadAssetTemplate = async (data: DownloadAssetTemplateRequest) => {
    return postBlob('/api/assets-templates/download', data);
  };

  const validateBulkTemplateUpload = async (
    fileType: AssetTemplateFileType,
    file: File
  ): Promise<BulkTemplateUploadValidationResult> => {
    const response = await post('/api/assets-templates/bulk/validate', buildBulkUploadPayload(fileType, file));
    return response.data;
  };

  const processBulkTemplateUpload = async (
    fileType: AssetTemplateFileType,
    file: File
  ): Promise<BulkTemplateUploadProcessResult> => {
    const response = await post('/api/assets-templates/bulk/process', buildBulkUploadPayload(fileType, file));
    return response.data;
  };

  const getFolderProvisioningStatus = async (): Promise<FolderProvisioningStatus> => {
    const response = await get('/api/assets-templates/bulk/folder-status');
    const raw = response.data ?? {};

    const toNumber = (value: unknown): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    return {
      pendingCount: toNumber(raw.pendingCount ?? raw.PendingCount),
      completedCount: toNumber(raw.completedCount ?? raw.CompletedCount),
      failedCount: toNumber(raw.failedCount ?? raw.FailedCount),
      totalCount: toNumber(raw.totalCount ?? raw.TotalCount),
    };
  };

  return {
    uploadAirframeTemplate,
    getAirframeTemplates,
    getAirframeTemplateById,
    downloadAssetTemplate,
    validateBulkTemplateUpload,
    processBulkTemplateUpload,
    getFolderProvisioningStatus,
  };
};
